import { html, TemplateResult, nothing, PropertyValueMap } from 'lit';
import { customElement } from 'lit/decorators.js';
import { SearchV3UIBase } from './search-v3-ui-base';
import { SearchV3Result } from '../search-v3.interface';

/**
 * Search Page UI Component
 *
 * Extends the base SearchV3UIBase component to provide search page specific behavior:
 * - Suggestions are rendered as clickable buttons that trigger in-place search
 * - Clicking a suggestion dispatches search actions instead of navigation
 * - Results are loaded on the same page without redirecting
 * - Designed for use on search result pages where loadResults=true
 */
@customElement('mhecomm-search-v3-ui-search-page')
export class SearchV3UISearchPage extends SearchV3UIBase {
  /**
   * Update page title on first load if searchInput exists
   */
  protected firstUpdated(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
    super.firstUpdated(_changedProperties);
    if (this.searchInput) {
      document.title = `Search results for "${this.searchInput}" | McGraw Hill`;
    }
  }

  /**
   * Override the suggestion item rendering for search page behavior
   * Renders suggestions as clickable buttons that trigger search actions
   */
  protected renderSuggestionItem(
    suggestion: SearchV3Result,
    highlighted: boolean,
    index: number,
    handleSelect: (item: any) => void
  ): TemplateResult {
    return html`<button
      class="dropdown-item text-start ${highlighted ? 'bg-light' : ''}"
      id="item-${index}"
      role="option"
      aria-selected=${highlighted}
      @click=${() => handleSelect(suggestion)}
      tabindex="-1">
      ${suggestion.term} ${suggestion.label ? html`<span class="text-primary">${suggestion.label}</span>` : nothing}
    </button>`;
  }

  /**
   * Handles selection of suggestion options (search page behavior)
   * - Updates search input with selected suggestion
   * - Triggers analytics
   * - Calls onSearchTrigger to perform search
   */
  private handleSuggestionSelection(suggestion: SearchV3Result): void {
    // Update search input with selected suggestion
    this.updateSearchInput?.(suggestion.term);

    // Trigger analytics for search page behavior
    this.searchResultClickAnalytic?.(suggestion);

    // Call the parent's search submit handler to trigger search
    this.onSearchTrigger?.();
  }

  /**
   * Override input typeahead to use the search page specific renderItem method
   */
  protected inputTypeahead() {
    return html`<mhecomm-search-input
      minSearchChars="3"
      inputElClassList="${this.renderSmallUI ? 'border rounded-start border-gray600' : ''}"
      class="form-control position-relative ${this.renderSmallUI ? 'border-0' : 'search-border'}"
      .value=${this.searchInput}
      .updateSearchInput=${this.updateSearchInput?.bind(this)}
      .fetchFn=${this.onSearchInputChange.bind(this)}
      .getLabel=${(suggestion: any) => suggestion.term}
      .renderItem=${this.renderSuggestionItem.bind(this)}
      .onSelect=${this.handleSuggestionSelection.bind(this)}
      placeholder=${this.searchPlaceholder}
      ?hideSuggestion=${this.searchInput?.length <= 2}
      .blockEnterOnPendingInput=${false}
      .updatePageTitle=${true}
      .pageTitleTemplate=${'Search results for "%query%" | McGraw Hill'}
      defaultPageTitle="Search"></mhecomm-search-input>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-search-v3-ui-search-page': SearchV3UISearchPage;
  }
}
